import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { SEOHead } from '../../components/SEOHead';
import { BlogMeta } from '../../components/BlogMeta';
import { trackPageView } from '../../utils/analytics';

const CAUSES = [
  {
    title: 'Slow server response (high TTFB)',
    body: 'If the server takes a long time to respond with the first byte of HTML, everything downstream — including the LCP element — is delayed. A Time to First Byte (TTFB) above 600 ms is a strong signal that server-side performance needs attention.',
  },
  {
    title: 'Render-blocking resources',
    body: (
      <>CSS and synchronous JavaScript in the <code>&lt;head&gt;</code> block the browser from rendering any content until they have been downloaded and parsed. The longer these files take to load, the later the LCP element can appear.</>
    ),
  },
  {
    title: 'Slow resource load times',
    body: 'If the LCP element is an image hosted on a slow origin or is not properly compressed, the browser must wait for the full download before it can paint the element. Large, unoptimised images are the most common cause of a slow LCP.',
  },
  {
    title: 'Client-side rendering delays',
    body: 'Single-page applications (SPAs) that render content entirely in JavaScript often have a high LCP because the browser must download, parse, and execute the JavaScript bundle before any meaningful content appears on screen.',
  },
];

const STEPS = [
  {
    num: 1,
    title: 'Preload the LCP image',
    body: (
      <>
        <p className="text-slate-700 mb-3">Add a <code className="bg-slate-100 px-1 rounded">&lt;link rel="preload" as="image"&gt;</code> tag in the <code className="bg-slate-100 px-1 rounded">&lt;head&gt;</code> so the browser discovers and fetches the LCP image as early as possible, without waiting for it to be found in the HTML body or a CSS file.</p>
        <pre className="bg-slate-100 rounded p-4 text-sm overflow-x-auto">{`<link rel="preload" as="image" href="/hero.webp" fetchpriority="high">`}</pre>
      </>
    ),
  },
  {
    num: 2,
    title: 'Compress and correctly size images',
    body: <p className="text-slate-700">Convert images to WebP or AVIF format. Both formats deliver significantly smaller file sizes than JPEG or PNG at equivalent quality. Serve each image at the dimensions it will actually be displayed — do not rely on CSS to scale a 3000-pixel image down to 600 pixels.</p>,
  },
  {
    num: 3,
    title: 'Improve Time to First Byte (TTFB)',
    body: <p className="text-slate-700">Deploy your site behind a content delivery network (CDN) so that HTML is served from an edge node close to the user. Set appropriate <code className="bg-slate-100 px-1 rounded">Cache-Control</code> headers and, where possible, enable edge-side rendering or static generation so the server does not need to build the page on every request.</p>,
  },
  {
    num: 4,
    title: 'Remove render-blocking CSS and JavaScript',
    body: <p className="text-slate-700">Inline only the critical CSS needed to render above-the-fold content. Load the rest of the stylesheet asynchronously using <code className="bg-slate-100 px-1 rounded">media="print" onload="this.media='all'"</code>. Defer or move below the fold any JavaScript that is not required for the initial render.</p>,
  },
  {
    num: 5,
    title: 'Use responsive images with srcset and sizes',
    body: (
      <>
        <p className="text-slate-700">Provide multiple image resolutions via the <code className="bg-slate-100 px-1 rounded">srcset</code> attribute so mobile devices download a smaller file rather than a full-resolution image intended for a wide desktop viewport.</p>
        <pre className="bg-slate-100 rounded p-4 text-sm overflow-x-auto mt-3">{`<img
  src="/hero-800.webp"
  srcset="/hero-400.webp 400w, /hero-800.webp 800w, /hero-1600.webp 1600w"
  sizes="(max-width: 600px) 400px, (max-width: 1200px) 800px, 1600px"
  alt="Hero image"
>`}</pre>
      </>
    ),
  },
  {
    num: 6,
    title: 'Do not lazy-load the LCP element',
    body: <p className="text-slate-700">Lazy loading defers image fetching until the element is near the viewport — which is beneficial for below-the-fold images but harmful for the LCP element. Ensure your LCP image does not have <code className="bg-slate-100 px-1 rounded">loading="lazy"</code> and, where supported, add <code className="bg-slate-100 px-1 rounded">fetchpriority="high"</code>.</p>,
  },
  {
    num: 7,
    title: 'Use a content delivery network (CDN)',
    body: <p className="text-slate-700">Serving static assets — images, fonts, scripts — from a CDN reduces the physical distance between the user and the resource. Lower network latency directly shortens the time required to download the LCP image or font, improving the overall LCP score.</p>,
  },
  {
    num: 8,
    title: 'Enable HTTP/2 or HTTP/3',
    body: <p className="text-slate-700">HTTP/2 allows multiple requests to be multiplexed over a single connection, eliminating the head-of-line blocking that affected HTTP/1.1. HTTP/3 adds further improvements through the QUIC protocol, particularly on high-latency or lossy connections. Most modern CDNs and hosting providers support both protocols by default.</p>,
  },
];

export const HowToImproveLCP = () => {
  useEffect(() => {
    trackPageView('/blog/how-to-improve-lcp');
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title="How to Improve LCP: Fastest Ways to Pass the Core Web Vitals Threshold"
        description="Largest Contentful Paint (LCP) should be 2.5 seconds or less. Here are the most effective techniques to reduce LCP and improve your Core Web Vitals score."
        canonical="https://webspeedadvisor.com/blog/how-to-improve-lcp"
      />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/" className="inline-flex items-center gap-2 text-primary font-medium hover:underline mb-8 block">
          ← Back to Home
        </Link>

        <article className="space-y-12">
          <header>
            <h1 className="text-4xl md:text-5xl font-black font-chivo text-slate-900 mb-4">
              How to Improve LCP: Fastest Ways to Pass the Core Web Vitals Threshold
            </h1>
            <BlogMeta published="2025-03-12" readMinutes={8} />
            <p className="text-xl text-slate-600">
              Largest Contentful Paint (LCP) measures how quickly the main content of a page becomes visible. It is one of Google's three Core Web Vitals and part of the page experience signals Google uses in ranking systems. This guide explains what affects LCP and the most effective steps to bring it under the 2.5-second threshold.
            </p>
          </header>

          <section>
            <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-4">What Is LCP and Why Does It Matter?</h2>
            <div className="prose prose-lg max-w-none text-slate-700 space-y-4">
              <p>
                Largest Contentful Paint (LCP) marks the point in the page load timeline when the largest visible element — typically a hero image, a banner, or a prominent heading — has finished rendering. Google uses LCP as a direct measure of perceived load speed from the user's perspective.
              </p>
              <p>
                Google's thresholds are clear:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Good:</strong> 2.5 seconds or less</li>
                <li><strong>Needs improvement:</strong> 2.5 – 4.0 seconds</li>
                <li><strong>Poor:</strong> more than 4.0 seconds</li>
              </ul>
              <p>
                Pages that score "good" on LCP are more likely to rank well in Google Search and deliver a better experience to visitors. Pages that fall into the "poor" range typically show higher bounce rates and lower conversion rates.
              </p>
            </div>
          </section>

          <section className="bg-white rounded-xl p-8 shadow-sm">
            <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-4">What Counts as the LCP Element?</h2>
            <div className="prose prose-lg max-w-none text-slate-700 space-y-4">
              <p>
                The browser identifies the LCP element as the largest image, video poster frame, or block of text that is visible in the viewport when the page first loads. The measurement updates as the page loads until the user interacts with it, at which point the final LCP candidate is recorded.
              </p>
              <p>Common LCP elements include:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Hero images at the top of the page</li>
                <li>Above-the-fold banner images or carousels</li>
                <li>Large above-the-fold headings rendered in a custom font</li>
                <li>Video poster images</li>
              </ul>
              <p>
                You can identify your LCP element using Chrome DevTools (Performance panel) or by running a Lighthouse audit. Web Speed Advisor also surfaces the LCP element and its load time in the results report.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-4">Top Causes of Slow LCP</h2>
            <div className="bg-white rounded-xl p-8 shadow-sm space-y-6">
              {CAUSES.map(({ title, body }) => (
                <div key={title}>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{title}</h3>
                  <p className="text-slate-700">{body}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-4">How to Improve LCP</h2>
            <div className="prose prose-lg max-w-none text-slate-700">
              <p>Apply these steps in order of impact. Each one addresses a distinct part of the LCP timeline.</p>
            </div>
            <div className="mt-6 space-y-6">
              {STEPS.map(({ num, title, body }) => (
                <div key={num} className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{num}. {title}</h3>
                  {body}
                </div>
              ))}
            </div>
          </section>

          <section className="bg-slate-100 rounded-xl p-8">
            <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-4">LCP in Next.js and React Applications</h2>
            <div className="prose prose-lg max-w-none text-slate-700 space-y-4">
              <p>
                React SPAs that render entirely on the client side tend to score poorly on LCP because the browser must download and execute JavaScript before any content appears. The two most effective remedies are server-side rendering (SSR) and static site generation (SSG), both of which deliver pre-rendered HTML so the LCP element is available in the initial response.
              </p>
              <p>
                In Next.js specifically, the <code>next/image</code> component handles many LCP best practices automatically: it generates a <code>srcset</code>, converts images to WebP, and prevents layout shift by requiring explicit width and height values. For the LCP image, pass the <code>priority</code> prop, which causes Next.js to inject a preload link and set <code>fetchpriority="high"</code>:
              </p>
              <pre className="bg-white rounded p-4 text-sm overflow-x-auto">{`import Image from 'next/image';

<Image
  src="/hero.jpg"
  width={1200}
  height={600}
  alt="Hero"
  priority
/>`}</pre>
              <p>
                For non-Next.js React applications, consider adopting SSR via a framework such as Remix, or pre-rendering pages at build time to ensure the LCP element is included in the static HTML rather than injected by JavaScript.
              </p>
            </div>
          </section>

          <section className="bg-primary text-white rounded-xl p-8 text-center">
            <h2 className="text-3xl font-bold font-chivo mb-4">Check Your LCP Score</h2>
            <p className="text-lg mb-6 text-blue-100">
              Run a free analysis to see your current LCP value, identify the LCP element, and get prioritised recommendations to bring it under 2.5 seconds.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-primary font-bold px-8 py-4 rounded-full transition-all"
            >
              <Zap size={24} />
              Check Your LCP Score with Web Speed Advisor →
            </Link>
          </section>
        </article>
      </div>
    </div>
  );
};

export default HowToImproveLCP;
